// Drawer.tsx
import Offcanvas from 'react-bootstrap/Offcanvas';
import Accordion from 'react-bootstrap/Accordion';
import Form from 'react-bootstrap/Form';
import { useMemo } from 'react';

import { useDataStore, useVizStore } from '@/store';
import type { BillIndexEntry, MetricIndexEntry } from '@/types';
import BillCombo from './BillSearch';
import MetricCombo from './MetricSearch';

export function DataDrawer() {
  // Use individual selectors to avoid object comparison issues
  const billIndex = useDataStore((s) => s.billIndex);
  const billIndexLoaded = useDataStore((s) => s.billIndexLoaded);
  const metricIndex = useDataStore((s) => s.metricIndex);
  const metricIndexLoaded = useDataStore((s) => s.metricIndexLoaded);

  const congressId = useDataStore((s) => s.congressId);
  const billId = useDataStore((s) => s.billId);
  const metricKey = useDataStore((s) => s.metricKey);

  // Functions are stable in Zustand, so we can get them separately
  const setCongressId = useDataStore((s) => s.setCongressId);
  const setBillId = useDataStore((s) => s.setBillId);
  const setMetricKey = useDataStore((s) => s.setMetricKey);

  // Use individual selectors for viz store values to avoid object comparison issues
  // `dataDrawerOpen` is the boolean flag controlling the data drawer visibility
  const drawerOpen = useVizStore((s) => s.dataDrawerOpen);

  // Get functions separately - they're stable references
  const closeDrawer = useVizStore((s) => s.closeDataDrawer);

  // Options for the selectors (computed locally, no props)
  const congressOptions = useMemo<number[]>(() => {
    if (!billIndexLoaded || !billIndex?.length) return [];
    const set = new Set<number>();
    for (const row of billIndex) set.add(row.congress);
    return Array.from(set).sort((a, b) => b - a);
  }, [billIndexLoaded, billIndex]);

  const billOptions = useMemo<BillIndexEntry[]>(() => {
    if (!billIndexLoaded || !billIndex?.length || !congressId) return [];
    const cid = Number(congressId);
    if (!Number.isFinite(cid)) return [];
    return billIndex
      .filter((b) => b.congress === cid)
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [billIndexLoaded, billIndex, congressId]);

  // Build dropdown options from metric index
  const metricOptions = useMemo<MetricIndexEntry[]>(() => {
    if (!metricIndexLoaded || !metricIndex?.length || !congressId) return [];
    const cid = Number(congressId);
    if (!Number.isFinite(cid)) return [];
    return metricIndex
      .filter((m) => m.congress === cid)
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [metricIndexLoaded, metricIndex, congressId]);

  return (
    <Offcanvas placement="start" show={drawerOpen} onHide={closeDrawer}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Visualization Options</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Accordion defaultActiveKey={['data', 'map']} alwaysOpen>
          {/* DATA */}
          <Accordion.Item eventKey="data">
            <Accordion.Header>Data</Accordion.Header>
            <Accordion.Body>
              <Form>
                {/* Congress */}
                <div className="row align-items-center mb-3">
                  <Form.Label
                    htmlFor="select-congress"
                    className="col-4 col-form-label fw-semibold mb-0"
                  >
                    Congress
                  </Form.Label>
                  <div className="col-8">
                    <Form.Select
                      id="select-congress"
                      size="sm"
                      value={String(congressId)}
                      onChange={(e) => setCongressId(Number(e.target.value))}
                    >
                      {congressOptions.map((c) => (
                        <option key={c} value={String(c)}>
                          {c}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>

                {/* Bill */}
                <div className="row align-items-center mb-2">
                  <div>
                    <div className="d-flex align-items-center">
                      <Form.Label
                        htmlFor="toolbar-bill"
                        className="me-2 mb-0 fw-bold"
                        style={{ minWidth: '80px' }}
                      >
                        Bill
                      </Form.Label>
                      <div style={{ flex: '1 1 auto', overflow: 'visible', position: 'relative' }}>
                        <BillCombo items={billOptions} setBillId={setBillId} billId={billId} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metric */}
                <div className="row align-items-center mb-2">
                  <div>
                    <div className="d-flex align-items-center">
                      <Form.Label
                        htmlFor="toolbar-metric"
                        className="me-2 mb-0 fw-bold"
                        style={{ minWidth: '80px' }}
                      >
                        Map Feature
                      </Form.Label>
                      <div style={{ flex: '1 1 auto', overflow: 'visible', position: 'relative' }}>
                        <MetricCombo
                          items={metricOptions}
                          setMetricKey={setMetricKey}
                          metricKey={metricKey}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
